import React, { useRef } from 'react';
import html2pdf from 'html2pdf.js';
import './ResumePreview.css';

function ResumePreview({ personalInfo, professionalSummary, experience, education, skills, languageSkills, showDownloadButton = false }) {
  
  // PDF download functionality
  const resumeRef = useRef();

  const handleDownloadPDF = async () => {
    // Create isolated container for clean PDF generation
    const container = document.createElement('div');
    container.style.position = 'fixed';
    container.style.left = '0';
    container.style.top = '0';
    container.style.width = '794px'; // A4 width at 96 DPI
    container.style.zIndex = '-9999';
    container.style.visibility = 'hidden';
    
    // Clone the actual resume content
    const resumeContent = resumeRef.current.querySelector('.traditional-template');
    if (!resumeContent) {
      console.error('Resume template not found');
      return;
    }
    
    const clonedContent = resumeContent.cloneNode(true);
    clonedContent.style.background = 'white';
    clonedContent.style.width = '794px';
    clonedContent.style.padding = '15mm';
    clonedContent.style.boxSizing = 'border-box';
    
    container.appendChild(clonedContent);
    document.body.appendChild(container);
    
    // PDF generation options
    const opt = {
      margin: 0, // No margins - content controls its own spacing
      filename: `${personalInfo.fullName || 'Resume'}_Resume.pdf`,
      image: { 
        type: 'jpeg', 
        quality: 0.98 
      },
      html2canvas: { 
        scale: 2,
        useCORS: true,
        letterRendering: true,
        logging: false,
        backgroundColor: '#ffffff',
        windowWidth: 794,
        scrollX: 0,
        scrollY: 0,
        timeout: 300
      },
      jsPDF: { 
        unit: 'mm', 
        format: 'a4', 
        orientation: 'portrait',
        compress: true
      },
      pagebreak: { 
        mode: ['css', 'legacy'],
        before: '.page-break-before',
        after: '.page-break-after',
        avoid: '.avoid-break'
      }
    };

    try {
      await html2pdf().set(opt).from(clonedContent).save();
    } catch (error) {
      console.error('PDF generation error:', error);
    } finally {
      // Clean up
      document.body.removeChild(container);
    }
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return '';
    
    // Handle YYYY-MM format (from HTML month input)
    const match = dateString.match(/^(\d{4})-(\d{2})$/);
    if (match) {
      const year = match[1];
      const monthIndex = parseInt(match[2], 10) - 1; // 0-based month
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      return `${monthNames[monthIndex]} ${year}`;
    }
    
    // Fallback for other formats - parse without adding day to avoid timezone issues
    const parts = dateString.split('-');
    if (parts.length >= 2) {
      const year = parts[0];
      const monthIndex = parseInt(parts[1], 10) - 1;
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      return `${monthNames[monthIndex]} ${year}`;
    }
    
    return dateString;
  };

  // Render based on selected template
  const renderTraditionalTemplate = () => (
    <div className="resume-preview traditional-template">
      {/* Header - Keep together */}
      <div className="resume-header-traditional" style={{ pageBreakInside: 'avoid', breakInside: 'avoid' }}>
        <h1 className="resume-name-traditional">{personalInfo.fullName || 'Your Name'}</h1>
        <div className="resume-contact-traditional">
          {personalInfo.email && <span>{personalInfo.email}</span>}
          {personalInfo.phone && <span> | {personalInfo.phone}</span>}
          {personalInfo.location && <span> | {personalInfo.location}</span>}
        </div>
        {(personalInfo.linkedIn || personalInfo.portfolio) && (
          <div className="resume-links-traditional">
            {personalInfo.linkedIn && <span>{personalInfo.linkedIn}</span>}
            {personalInfo.portfolio && <span> | {personalInfo.portfolio}</span>}
          </div>
        )}
      </div>

      {/* Professional Summary - Keep together */}
      {professionalSummary && (
        <div className="resume-section-traditional" style={{ pageBreakInside: 'avoid', breakInside: 'avoid' }}>
          <h2 className="section-title-traditional">PROFESSIONAL SUMMARY</h2>
          <p className="summary-text-traditional">{professionalSummary}</p>
        </div>
      )}

      {/* Experience & Projects - Allow breaks between items, not within */}
      {experience.filter(exp => (exp.type === 'work' && exp.company && exp.position) || (exp.type === 'project' && exp.projectName)).length > 0 && (
        <div className="resume-section-traditional">
          <h2 className="section-title-traditional" style={{ pageBreakAfter: 'avoid', breakAfter: 'avoid' }}>EXPERIENCE & PROJECTS</h2>
          {experience.filter(exp => (exp.type === 'work' && exp.company && exp.position) || (exp.type === 'project' && exp.projectName)).map((exp, index) => (
            <div key={index} className="experience-entry-traditional" style={{ pageBreakInside: 'avoid', breakInside: 'avoid' }}>
              <div className="experience-item-traditional">
                <div className="experience-left-traditional">
                  <strong className="experience-position-traditional">{exp.position}</strong>
                  <span className="experience-company-traditional">{exp.company}</span>
                </div>
                <div className="experience-right-traditional">
                  {(exp.startDate || exp.endDate) && (
                    <span className="experience-dates-traditional">
                      {exp.startDate && formatDate(exp.startDate)}
                      {' - '}
                      {exp.current ? 'Present' : (exp.endDate ? formatDate(exp.endDate) : '')}
                    </span>
                  )}
                  {exp.location && <span className="experience-location-traditional">{exp.location}</span>}
                </div>
              </div>
              {exp.description && (
                <div className="experience-description-traditional">
                  {Array.isArray(exp.description)
                    ? exp.description.map((desc, i) => {
                        // Remove existing bullet symbols and add clean bullet
                        const cleanDesc = desc.replace(/^[-•]\s*/, '');
                        return <div key={i} style={{ marginBottom: '4px' }}>• {cleanDesc}</div>;
                      })
                    : typeof exp.description === 'string'
                    ? exp.description.split(/\s*-\s*/).filter(item => item.trim()).map((desc, i) => {
                        const cleanDesc = desc.replace(/^[-•]\s*/, '').trim();
                        return <div key={i} style={{ marginBottom: '4px' }}>• {cleanDesc}</div>;
                      })
                    : exp.description
                  }
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Education - Allow breaks between items, not within */}
      {education.filter(edu => edu.school && edu.degree).length > 0 && (
        <div className="resume-section-traditional">
          <h2 className="section-title-traditional" style={{ pageBreakAfter: 'avoid', breakAfter: 'avoid' }}>EDUCATION</h2>
          {education.filter(edu => edu.school && edu.degree).map((edu, index) => (
            <div key={index} className="education-item-traditional" style={{ pageBreakInside: 'avoid', breakInside: 'avoid' }}>
              <div className="education-left-traditional">
                <strong className="education-degree-traditional">{edu.degree}{edu.field ? ` in ${edu.field}` : ''}</strong>
                <span className="education-university-traditional">{edu.school}</span>
                {edu.gpa && <span className="education-gpa-traditional">GPA: {edu.gpa}</span>}
              </div>
              <div className="education-right-traditional">
                {(edu.startYear || edu.graduationYear) && (
                  <span className="education-dates-traditional">
                    {edu.startYear && edu.graduationYear 
                      ? `${formatDate(edu.startYear)} - ${formatDate(edu.graduationYear)}`
                      : (edu.startYear ? formatDate(edu.startYear) : formatDate(edu.graduationYear))
                    }
                  </span>
                )}
                {edu.location && <span className="education-location-traditional">{edu.location}</span>}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Skills - Keep together */}
      {skills && (
        <div className="resume-section-traditional" style={{ pageBreakInside: 'avoid', breakInside: 'avoid' }}>
          <h2 className="section-title-traditional">SKILLS</h2>
          <p className="skills-text-traditional">{skills}</p>
        </div>
      )}

      {/* Languages - Keep together */}
      {languageSkills && languageSkills.filter(lang => lang.language && lang.proficiency).length > 0 && (
        <div className="resume-section-traditional" style={{ pageBreakInside: 'avoid', breakInside: 'avoid' }}>
          <h2 className="section-title-traditional">LANGUAGES</h2>
          <p className="skills-text-traditional">
            {languageSkills
              .filter(lang => lang.language && lang.proficiency)
              .map((lang, index) => {
                // Capitalize first letter of language and proficiency
                const capitalizedLang = lang.language.charAt(0).toUpperCase() + lang.language.slice(1);
                const capitalizedProf = lang.proficiency.charAt(0).toUpperCase() + lang.proficiency.slice(1);
                return `${capitalizedLang} (${capitalizedProf})`;
              })
              .join(', ')}
          </p>
        </div>
      )}
    </div>
  );

  // Render the selected template
  return (
    <div className="resume-preview-container">
      <div ref={resumeRef} className="pdf-resume-container">
        {renderTraditionalTemplate()}
      </div>
    </div>
  );
}

export default ResumePreview;
